/**
 * Hosted edition, signed out. Sign-in happens on AuthKit's page (the Worker's /auth/login sends the browser there and
 * back), so this is only the way in. Plain links: a full page load, like any sign-in redirect.
 */
import { useEffect, useState } from 'react';
import { Logo } from '@/components/Logo';
import { Banner, Loading } from '@/components/ui';
import { isBluefy } from '@/lib/platform';
import { withArticle } from '@/scale/bluetooth';
import { BLUETOOTH_DRIVERS } from '@/scale/registry';
import './auth.css';

type SignInError = { tone: 'danger' | 'info'; message: string; code?: string };

/** What the Worker's /auth/callback put in `?signin_error`, in words. Codes we don't know show as a footnote. */
export function signInError(code: string): SignInError {
  if (code === 'expired') return { tone: 'danger', message: 'That sign-in took too long or was opened in another browser. Try again.' };
  if (code === 'access_denied') return { tone: 'info', message: 'Sign-in was cancelled.' };
  return { tone: 'danger', message: 'Couldn’t sign you in. Try again.', code: code.slice(0, 60) };
}

export function signInHref(screen?: 'sign-up'): string {
  const here = `${window.location.pathname}${window.location.search}`;
  const params = new URLSearchParams({ returnTo: here.startsWith('/?signin_error') ? '/' : here });
  if (screen) params.set('screen', screen);
  return `/auth/login?${params.toString()}`;
}

/** "A, B and C" / "A or B". */
function listText(items: readonly string[], last: 'and' | 'or'): string {
  return items.length <= 1 ? (items[0] ?? '') : `${items.slice(0, -1).join(', ')} ${last} ${items[items.length - 1]}`;
}

/** What a new account needs, from the scale drivers: "a Bookoo scale (Decent, Felicita and Acaia are experimental)". */
export function scaleRequirement(drivers: ReadonlyArray<{ shortName: string; experimental?: boolean }> = BLUETOOTH_DRIVERS): string {
  const tested = drivers.filter((d) => !d.experimental).map((d) => d.shortName);
  const experimental = drivers.filter((d) => d.experimental).map((d) => d.shortName);
  if (tested.length === 0) return `${withArticle(listText(experimental, 'or'))} scale (experimental)`;
  const note = experimental.length > 0 ? ` (${listText(experimental, 'and')} ${experimental.length === 1 ? 'is' : 'are'} experimental)` : '';
  return `${withArticle(listText(tested, 'or'))} scale${note}`;
}

function readSignInError(): SignInError | null {
  const code = new URLSearchParams(window.location.search).get('signin_error');
  return code ? signInError(code) : null;
}

/** While /api/info decides between the app and the sign-in screen: the logo and a status line. A quick answer shows
 * nothing at all, so the screen doesn't flash on every load. */
export function StartupScreen() {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => setShow(true), 300);
    return () => clearTimeout(timer);
  }, []);

  return (
    <main className="signin">
      {show && (
        <div className="signin-card stack-sm center">
          <div className="signin-title">
            <Logo height={40} title="Taretto" />
          </div>
          <Loading />
        </div>
      )}
    </main>
  );
}

export function SignInScreen() {
  // Read once: the URL is cleaned below, so a reload doesn't bring the message back.
  const [error] = useState(readSignInError);

  useEffect(() => {
    if (!error) return;
    const url = new URL(window.location.href);
    url.searchParams.delete('signin_error');
    window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
  }, [error]);

  useEffect(() => {
    const previous = document.title;
    document.title = 'Sign in · Taretto';
    return () => {
      document.title = previous;
    };
  }, []);

  return (
    <main className="signin">
      <div className="signin-card stack-lg">
        <div className="stack-sm center">
          <h1 className="signin-title">
            <Logo height={40} title="Taretto" />
          </h1>
          <p className="muted">Brew with your Bluetooth scale, and keep every shot, bean and grind setting.</p>
        </div>

        {error && (
          <Banner tone={error.tone}>
            {error.message}
            {error.code && <span className="signin-error-code text-sm muted">Error code: {error.code}</span>}
          </Banner>
        )}

        <div className="stack">
          <a className="btn primary lg block" href={signInHref()}>
            Sign in
          </a>
          <p className="text-sm muted center signin-needs">
            New here? You’ll need Chrome or Edge (computer or Android) or Bluefy (iPhone and iPad) for Bluetooth, and {scaleRequirement()}.
          </p>
          <a className="btn ghost block" href={signInHref('sign-up')}>
            Create an account
          </a>
        </div>

        <p className="text-sm muted center">
          Signing in sends a code to your email. No password needed.
          {isBluefy() && ' In Bluefy, use the email code rather than Google or a passkey: those don’t work in Bluefy.'}
        </p>
      </div>
    </main>
  );
}
